import React, { useCallback, useEffect, useState } from "react";
import { Eye, Globe, RefreshCw, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Builder Lead Inbox — every AI preview a visitor generates, with what they
 * asked for and what it's worth. Market research on autopilot: see demand
 * before prospects ever write an email.
 */
export default function AdminPreviews() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/builder-previews").then((x) => x.data);
      setItems(r);
    } catch { toast.error("Could not load previews"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const viewHtml = async (id) => {
    setBusy(id);
    try {
      const doc = await api.get(`/admin/builder-previews/${id}`).then((x) => x.data);
      if (!doc.html) { toast.error("No HTML stored for this preview"); return; }
      const blob = new Blob([doc.html], { type: "text/html" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not load preview"); }
    finally { setBusy(""); }
  };

  const ready = (items || []).filter((p) => p.status === "ready");
  const totalValue = ready.reduce((s, p) => s + ((p.estimate || {}).total || 0), 0);

  return (
    <div data-testid="admin-previews">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h2 className="font-display text-3xl">AI Preview Inbox</h2>
        <button onClick={load} data-testid="previews-refresh" className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:border-primary hover:text-primary transition">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <p className="text-muted-foreground text-sm mb-8 max-w-3xl">
        Every website a visitor generates with the AI builder lands here — what they described,
        what it's worth, and the preview itself. These are your warmest future leads.
      </p>

      {items && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="previews-stats">
          <div className="border border-border bg-card p-4"><p className="font-display text-2xl">{items.length}</p><p className="text-xs text-muted-foreground">Previews generated</p></div>
          <div className="border border-border bg-card p-4"><p className="font-display text-2xl">{ready.length}</p><p className="text-xs text-muted-foreground">Completed</p></div>
          <div className="border border-border bg-card p-4"><p className="font-display text-2xl">{ready.filter((p) => (p.refinements || []).length > 0).length}</p><p className="text-xs text-muted-foreground">Refined (high intent)</p></div>
          <div className="border border-border bg-card p-4"><p className="font-display text-2xl">${totalValue.toLocaleString()}</p><p className="text-xs text-muted-foreground">Combined estimate value</p></div>
        </div>
      )}

      <div className="space-y-3" data-testid="previews-list">
        {!items && <p className="p-6 border border-border text-muted-foreground">Loading…</p>}
        {items && items.length === 0 && (
          <p className="p-6 border border-border text-muted-foreground">No previews yet — they'll appear the moment a visitor uses the AI builder.</p>
        )}
        {(items || []).map((p) => (
          <div key={p.id} className="border border-border bg-card p-5" data-testid={`preview-row-${p.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider border px-2 py-1 ${
                p.status === "ready" ? "text-green-700 border-green-300 bg-green-50" :
                p.status === "generating" || p.status === "refining" ? "text-blue-600 border-blue-300 bg-blue-50" :
                "text-red-700 border-red-300 bg-red-50"}`}>
                <Sparkles className="w-3 h-3" /> {p.status}
              </span>
              <span className="text-xs text-muted-foreground font-mono">{(p.created_at || "").slice(0, 16).replace("T", " ")}</span>
              {(p.refinements || []).length > 0 && (
                <span className="text-[11px] font-mono text-primary border border-primary/40 px-2 py-0.5">{p.refinements.length} refinement{p.refinements.length > 1 ? "s" : ""}</span>
              )}
              {p.estimate?.total > 0 && (
                <span className="text-[11px] font-mono text-muted-foreground">est. ${p.estimate.total.toLocaleString()}</span>
              )}
            </div>
            <p className="text-sm leading-relaxed">{(p.input || {}).description || "—"}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {(p.input || {}).business_type} · {(p.input || {}).size} · {(p.input || {}).design}</span>
              {((p.input || {}).features || []).length > 0 && <span>features: {p.input.features.join(", ")}</span>}
              {p.status === "ready" && (
                <button onClick={() => viewHtml(p.id)} disabled={busy === p.id} data-testid={`preview-view-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline disabled:opacity-60">
                  <Eye className="w-3.5 h-3.5" /> {busy === p.id ? "Opening…" : "View generated site"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
